import { endOfDay, startOfDay } from "date-fns";

import { formatDateTime } from "@performyard/py-components";

import type { Filter } from "../../../components/FilterChips/FilterChips";

import type { SearchServiceSort } from "../../../api/hooks/defs/search-service";

import type { GetMeetingsSearchParams } from "../../../api/services/defs/internal-api";

import { capitalizeWord } from "../../../utils/stringUtils";

import { config } from "./config";
import type { MeetingsTableFilters } from "./schema";

type ConvertFiltersParams = {
	filters: MeetingsTableFilters;
	sort: SearchServiceSort;
	pageNumber: number;
	workspaceId: string;
	userId: string;
	searchTerm?: string;
};

/**
 * Convert the filters, sort, page number, and search term from the UI into the search service API params.
 */
export const convertFiltersToSearchParams = (params: ConvertFiltersParams): GetMeetingsSearchParams => {
	const { filters, sort, pageNumber, workspaceId, userId, searchTerm } = params;

	const searchParams: GetMeetingsSearchParams = {
		meetingWorkspaceId: workspaceId,
		limit: config.pageSize,
		skip: (pageNumber - 1) * config.pageSize,
		sort: { name: sort.name as "startDateTime" | "name" | "status", direction: sort.direction },
	};

	if (filters.status && filters.status.length > 0) {
		searchParams.status = filters.status;
	}

	// Private notes filter logic - add the userId for filtering user-specific meetings
	if (filters.privateNotes && filters.privateNotes !== "any") {
		searchParams.participantId = userId;
		searchParams.privateNotesFilter = filters.privateNotes;
	}

	if (filters.recurringSeriesId) {
		searchParams.recurringSeriesId = filters.recurringSeriesId;
	}

	// Date range filters - apply start-of-day/end-of-day logic
	if (filters.meetingDateStart) {
		searchParams.dateRangeStart = startOfDay(filters.meetingDateStart);
	}

	if (filters.meetingDateEnd) {
		searchParams.dateRangeEnd = endOfDay(filters.meetingDateEnd);
	}

	// Search term for meeting name
	if (searchTerm && searchTerm.trim() !== "") {
		searchParams.name = searchTerm.trim();
	}

	return searchParams;
};

/**
 * Convert the filters from the UI into the filter chips schema for display.
 */
export const convertFiltersToFilterChips = (filters: MeetingsTableFilters, recurringSeriesName?: string): Filter[] => {
	const filterChips: Filter[] = [];

	if (filters.status && filters.status.length > 0) {
		filterChips.push({
			id: "status",
			type: "string",
			label: "Status",
			value: filters.status.map((s) => capitalizeWord(s)).join(", "),
		});
	}

	if (filters.recurringSeriesId) {
		filterChips.push({
			id: "recurringSeries",
			type: "string",
			label: "Recurring Meeting Series",
			value: recurringSeriesName ?? "Selected series",
		});
	}

	if (filters.privateNotes && filters.privateNotes !== "any") {
		const label = filters.privateNotes === "has-private-notes" ? "Has Private Notes" : "No Private Notes";
		filterChips.push({
			id: "privateNotes",
			type: "string",
			label: "Private Notes",
			value: label,
		});
	}

	if (filters.meetingDateStart || filters.meetingDateEnd) {
		const startDate = filters.meetingDateStart
			? formatDateTime(filters.meetingDateStart, { hasTime: false, dateSize: "large" })
			: "";
		const endDate = filters.meetingDateEnd
			? formatDateTime(filters.meetingDateEnd, { hasTime: false, dateSize: "large" })
			: "";
		const label = startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate;
		filterChips.push({
			id: "meetingDate",
			type: "string",
			label: "Meeting Date",
			value: label || "",
		});
	}

	return filterChips;
};

/**
 * Determine if any filters are applied.
 */
export const getHasFiltersApplied = (filters: MeetingsTableFilters): boolean => {
	const hasStatusFilter = Boolean(filters.status && filters.status.length > 0);
	const hasDateRangeFilter = Boolean(filters.meetingDateStart || filters.meetingDateEnd);
	const hasPrivateNotesFilter = Boolean(filters.privateNotes && filters.privateNotes !== "any");
	const hasRecurringSeriesFilter = Boolean(filters.recurringSeriesId);

	return hasStatusFilter || hasDateRangeFilter || hasPrivateNotesFilter || hasRecurringSeriesFilter;
};

/**
 * Export filters format expected by the Lambda export function.
 * Property names must match what the Lambda's dto.ts expects.
 */
export type ExportFilters = {
	status?: string[];
	dateRangeStart?: string;
	dateRangeEnd?: string;
	privateNotes?: string;
	participantId?: string;
	recurringSeriesId?: string;
};

/**
 * Convert UI filters to the format expected by the export Lambda.
 * The Lambda expects different property names than the UI uses.
 */
export const convertFiltersToExportParams = (filters: MeetingsTableFilters, userId?: string): ExportFilters => {
	const exportFilters: ExportFilters = {};

	if (filters.status && filters.status.length > 0) {
		exportFilters.status = filters.status;
	}

	// Convert meetingDateStart/End to dateRangeStart/End with start-of-day/end-of-day
	if (filters.meetingDateStart) {
		exportFilters.dateRangeStart = startOfDay(filters.meetingDateStart).toISOString();
	}

	if (filters.meetingDateEnd) {
		exportFilters.dateRangeEnd = endOfDay(filters.meetingDateEnd).toISOString();
	}

	// Only include privateNotes if it has a meaningful value
	// Also include participantId so the lambda can filter by user-specific notes
	if (filters.privateNotes && filters.privateNotes !== "any") {
		exportFilters.privateNotes = filters.privateNotes;
		if (userId) {
			exportFilters.participantId = userId;
		}
	}

	if (filters.recurringSeriesId) {
		exportFilters.recurringSeriesId = filters.recurringSeriesId;
	}

	return exportFilters;
};

/**
 * Get appropriate error toast message based on operation data
 * Provides status-specific and operation-specific error messages
 */
export const getErrorToastMessage = (operationData: {
	status?: string;
	addToCalendar?: boolean;
	removeFromCalendar?: boolean;
	videoConferencingProvider?: string | null;
}): string => {
	// Provide status-specific error messages
	if (operationData.status) {
		switch (operationData.status) {
			case "archived":
				return "Failed to Archive Meeting";
			case "canceled":
				return "Failed to Cancel Meeting";
			case "completed":
				return "Failed to Complete Meeting";
			case "active":
				return "Failed to Reopen Meeting";
		}
	}

	// Check for specific calendar operations
	if (operationData.addToCalendar) {
		return "Failed to Add Meeting to Calendar";
	}

	if (operationData.removeFromCalendar) {
		return "Failed to Remove Meeting from Calendar";
	}

	// Fallback only — a VC failure usually carries a backend message the caller prefers.
	if (operationData.videoConferencingProvider) {
		return "Failed to Add Video Conferencing";
	}

	// Default error messages
	return "Failed to Update Meeting";
};

/**
 * Get appropriate success toast message based on operation and calendar involvement
 * This handles the complex logic for meeting updates with calendar integration
 *
 * @param params - The parameters for generating the success message
 * @param params.calendarOperationType - The type of calendar operation performed
 * @param params.operationData - Data about the operation (status, calendar flags)
 * @param params.isInitialCreation - Whether this is during initial meeting creation
 * @returns The appropriate success toast message
 */
export const getCalendarSuccessToastMessage = (params: {
	calendarOperationType: string | undefined;
	operationData: {
		addToCalendar?: boolean;
		removeFromCalendar?: boolean;
		status?: string;
		videoConferencingProvider?: string | null;
	};
	isInitialCreation?: boolean;
}): string => {
	const { calendarOperationType, operationData, isInitialCreation = false } = params;

	// Handle status changes first
	if (operationData.status) {
		switch (operationData.status) {
			case "archived":
				return "Meeting Archived";
			case "completed":
				return "Meeting Completed";
			case "canceled":
				// Check if calendar event was also removed
				if (operationData.removeFromCalendar) {
					return "Meeting Canceled and Removed from Calendar";
				} else {
					return "Meeting Canceled";
				}
			default:
				return "Meeting Updated";
		}
	}

	// Adding video conferencing to an existing meeting (options menus / add-VC modal — PY-12435).
	if (operationData.videoConferencingProvider && !isInitialCreation) {
		return "Video Conferencing Added to Meeting";
	}

	// Handle calendar-specific operations
	if (operationData.addToCalendar) {
		// Only show "Meeting Created and Added to Calendar" during initial meeting creation
		if (calendarOperationType === "created" && isInitialCreation) {
			return "Meeting Created and Added to Calendar";
		} else {
			return "Meeting Added to Calendar";
		}
	}

	// Handle calendar removal
	if (operationData.removeFromCalendar) {
		return "Meeting Removed from Calendar";
	}

	// Handle calendar update
	if (calendarOperationType === "updated") {
		return "Meeting Updated and Synced With Calendar";
	}

	// Handle calendar event not found (handled as error toast separately)
	if (calendarOperationType === "calendar_event_not_found") {
		return ""; // This will be handled as an error toast separately
	}

	// Default success messages based on operation type
	if (calendarOperationType === "created") {
		return "Meeting Created";
	}

	// Default success messages
	return "Meeting Updated";
};
